import { photoUrl } from "../../lib/api";
import { PlacementLink } from "../PlacementLink";
import { Button } from "../ui/button";
import { ExternalLink, Check, X, Clock, Layers, Mail } from "lucide-react";
import { AdminTag } from "./adShared";
import { useSiteEmails } from "../../lib/siteContent";

export function PendingAds({ pending, onMarkPaid, onMarkBundlePaid, onReject }) {
  const emails = useSiteEmails();
  const markPaid = onMarkPaid, markBundlePaid = onMarkBundlePaid, reject = onReject;
  const pendingBundles = {};
  const pendingSingles = [];
  for (const ad of pending) {
    if (ad.bundle_id) (pendingBundles[ad.bundle_id] = pendingBundles[ad.bundle_id] || []).push(ad);
    else pendingSingles.push(ad);
  }
  const bundleGroups = Object.entries(pendingBundles);
  return (
        <div className="bg-white rounded-2xl border border-blue-200 p-5 mb-6" data-testid="ad-pending-section">
          <h3 className="font-display font-bold text-[#12234A] flex items-center gap-2 mb-4"><Clock size={18} className="text-blue-600" /> Advertiser submissions awaiting approval <span className="text-xs font-bold bg-blue-100 text-blue-700 rounded-full px-2 py-0.5">{pending.length}</span></h3>
          <div className="space-y-3">
            {bundleGroups.map(([bid, group]) => {
              const g0 = group[0];
              const total = group.reduce((s, a) => s + (a.amount || 0), 0);
              return (
                <div key={bid} className="border border-[#F5551E]/30 bg-[#FFF9F6] rounded-xl p-3" data-testid={`ad-pending-bundle-${bid}`}>
                  <div className="flex items-start justify-between gap-2 flex-wrap mb-2">
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-[#12234A] inline-flex items-center gap-1.5"><Layers size={15} className="text-[#F5551E]" /> Bundle · {group.length} slots <span className="text-[10px] font-bold bg-emerald-100 text-emerald-700 rounded-full px-2 py-0.5">{g0.bundle_discount}% off</span></p>
                      <p className="text-[11px] text-slate-500 mt-0.5">{g0.owner_name} · Ref <strong className="font-mono">{g0.payment_ref}</strong> · <strong>{g0.currency} ${total.toFixed(2)}</strong> total to {emails.payment}</p>
                    </div>
                    <Button size="sm" onClick={() => markBundlePaid(bid)} data-testid={`ad-bundle-markpaid-${bid}`} className="rounded-lg h-8 bg-emerald-600 hover:bg-emerald-700 text-white gap-1 shrink-0"><Check size={14} /> Mark all {group.length} paid & go live</Button>
                  </div>
                  <div className="space-y-2">
                    {group.map((ad) => (
                      <div key={ad.id} className="border border-blue-100 bg-white rounded-lg p-2.5 flex gap-3" data-testid={`ad-pending-${ad.id}`}>
                        <img src={photoUrl(ad.image)} alt="" className="h-12 w-20 rounded-md object-cover bg-slate-100 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <p className="text-[13px] font-semibold text-[#12234A] truncate">{ad.title || "(untitled ad)"}</p>
                              {ad.created_by_admin && <AdminTag id={ad.id} />}
                              <p className="text-[11px] text-slate-500"><PlacementLink placement={ad.placement} className="text-slate-500" newTab /> · {ad.days} days · {ad.currency} ${ad.amount}</p>
                            </div>
                            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 shrink-0">{ad.payment_status === "paid" ? "Paid" : "Awaiting"}</span>
                          </div>
                          <Button size="sm" variant="outline" onClick={() => reject(ad.id)} data-testid={`ad-reject-${ad.id}`} className="mt-1.5 rounded-lg h-7 gap-1 text-red-600 border-red-200 hover:bg-red-50 text-[11px]"><X size={12} /> Reject this slot</Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
            {pendingSingles.map((ad) => (
              <div key={ad.id} className="border border-blue-100 bg-blue-50/40 rounded-xl p-3 flex gap-3" data-testid={`ad-pending-${ad.id}`}>
                <img src={photoUrl(ad.image)} alt="" className="h-16 w-24 rounded-lg object-cover bg-slate-100 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-[#12234A] truncate">{ad.title || "(untitled ad)"}</p>
                      {ad.created_by_admin && <AdminTag id={ad.id} />}
                      <p className="text-[11px] text-slate-500">{ad.owner_name} · <PlacementLink placement={ad.placement} className="text-slate-500" newTab /> · {ad.days} days</p>
                      <a href={ad.link_url} target="_blank" rel="noopener noreferrer" className="text-[11px] text-[#F5551E] inline-flex items-center gap-0.5 truncate hover:underline"><ExternalLink size={10} /> {ad.link_url}</a>
                    </div>
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 shrink-0">{ad.payment_status === "paid" ? "Paid" : "Awaiting e-Transfer"}</span>
                  </div>
                  <p className="text-[11px] text-slate-600 mt-1.5 inline-flex items-center gap-1"><Mail size={12} className="text-slate-400" /> Ref <strong>{ad.payment_ref}</strong> · <strong>{ad.currency} ${ad.amount}</strong> to {emails.payment}</p>
                  <div className="flex items-center gap-2 mt-2">
                    <Button size="sm" onClick={() => markPaid(ad.id)} data-testid={`ad-approve-${ad.id}`} className="rounded-lg h-8 bg-emerald-600 hover:bg-emerald-700 text-white gap-1"><Check size={14} /> Mark e-Transfer received & go live</Button>
                    <Button size="sm" variant="outline" onClick={() => reject(ad.id)} data-testid={`ad-reject-${ad.id}`} className="rounded-lg h-8 gap-1 text-red-600 border-red-200 hover:bg-red-50"><X size={14} /> Reject</Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
  );
}
